/**
 * DataCorrectionHelp — "How data corrections are applied", opened from the
 * blinking "?" on the Add instance card.
 *
 * Port of legacy ManagePortfolio/ActionButtons.vue (dialog #update-all-help,
 * :37-104). Not persistent (a click outside, Esc or ✕ closes it), no footer —
 * as legacy.
 *
 * One component for both products (.claude/rules/variants.md): CCA's longer
 * text (:49-94 — Cloud Selection, Quantity, Number of Hours per Month, Pricing
 * Model) sits behind <VariantGate only="cca">, and EIA's single rule (:95-102)
 * is its fallback. Both texts are legacy's, word for word, except one fix:
 * legacy's CCA text said "only two pricing models are supported: on-demand and
 * reserved", but the pricing model list (and Find and Replace) offers three —
 * on-demand, reserved (1 year) and reserved (3 year).
 */
import { Modal } from '@components/ui';
import { VariantGate } from '@variant/VariantGate';

import styles from './DataCorrectionHelp.module.css';

/** CCA rules, legacy's order: a plain sentence or a bullet list under each heading. */
const CCA_RULES = Object.freeze([
  {
    term: 'Cloud Selection:',
    text: 'If the cloud is empty, invalid, or unsupported, it will be replaced with the default CSP selected.',
  },
  {
    term: 'Quantity:',
    points: [
      'The quantity should be a positive number. If a floating-point number is provided, it will be rounded off.',
      'If the value is not mentioned, it will default to 1.',
    ],
  },
  {
    term: 'Number of Hours per Month:',
    points: [
      'If the value is empty, it will be set to (quantity * 730). For example, if the quantity is 5 and the number of hours per month is not mentioned, it will be auto-corrected to (5 * 730) = 3650.',
      'If the value exceeds (quantity * 730), it will automatically be set to (quantity * 730).',
      'If the value is a floating-point number, it will be rounded off.',
    ],
  },
  {
    term: 'Pricing Model:',
    points: [
      'Currently, three pricing models are supported: on-demand, reserved (1 year) and reserved (3 year).',
      'If the pricing model is empty, it will default to on-demand.',
      'If the value is something other than the supported options, the user can replace it with on-demand, reserved (1 year) or reserved (3 year) using the “find and replace” option.',
    ],
  },
]);

function EiaHelp() {
  return (
    <dl className={styles.list}>
      <dt className={styles.term}>Cloud Selection:</dt>
      <dd className={styles.text}>
        If the cloud value is empty, invalid, or unsupported, it will be automatically set to the
        default Cloud Service Provider (CSP).
      </dd>
    </dl>
  );
}

function CcaHelp() {
  return (
    <ol className={styles.steps}>
      {CCA_RULES.map(({ term, text, points }) => (
        <li key={term} className={styles.step}>
          <span className={styles.term}>{term}</span>
          {text ? <p className={styles.text}>{text}</p> : null}
          {points ? (
            <ul className={styles.points}>
              {points.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          ) : null}
        </li>
      ))}
    </ol>
  );
}

export function DataCorrectionHelp({ onClose }) {
  return (
    <Modal open onClose={onClose} title="How data corrections are applied">
      <VariantGate only="cca" fallback={<EiaHelp />}>
        <CcaHelp />
      </VariantGate>
    </Modal>
  );
}

export default DataCorrectionHelp;
